import { useEffect, useRef } from 'react'
import { scenePositionToLngLat } from '@/lib/utils/project-to-screen'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { placementScenePosition } from '../motion/placementPose'
import type { MovementPlan } from '../motion/movementPlan'

interface UseEventSelectionCameraOptions {
  map: mapboxgl.Map | null
  mapCenter: { lat: number; lng: number } | undefined
  plans: Record<string, MovementPlan>
}

/**
 * When a plane is selected, ease the camera so it sits horizontally centered and
 * ~1/4 up from the bottom of the screen — leaving the upper area free for the
 * anchored info popover. Preserves the current zoom/bearing/pitch. Fires only on
 * the selection *changing* (not on every clock tick) so playback isn't fought.
 */
export function useEventSelectionCamera({
  map,
  mapCenter,
  plans,
}: UseEventSelectionCameraOptions) {
  const selectedId = useEventPlannerStore((s) => s.selectedAllocationId)
  const lastFramedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!map || !mapCenter) return
    if (!selectedId) {
      lastFramedRef.current = null
      return
    }
    if (lastFramedRef.current === selectedId) return
    lastFramedRef.current = selectedId

    const store = useEventPlannerStore.getState()
    const p = store.placements[selectedId]
    if (!p) return

    const pos = placementScenePosition(p, plans, store.currentTime, store.motionConfig.enableAnimation)
    const center = scenePositionToLngLat(pos, mapCenter)

    const canvas = map.getCanvas()
    // Positive y offset pushes the centered point downward on screen, so the
    // plane lands ~3/4 of the way down (1/4 from the bottom).
    const offsetY = canvas.clientHeight / 4

    map.easeTo({ center, offset: [0, offsetY], duration: 600 })
  }, [map, mapCenter, plans, selectedId])

  return null
}
